import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { DocumentsIcon } from "../../../Icons";
import { APPLICATION_THEME } from "../applicationTheme";

/**
 * Shown when an employee clicks a library form they have already submitted.
 * Actions: view submitted forms (HR Documents), edit the latest copy, or
 * start a new copy when the form allows multiple submissions.
 */
const AlreadyFilledFormModal = ({
  isOpen,
  formTitle,
  onClose,
  onGoToForms,
  onFillAgain,
  onAddNewForm,
  allowMultiple = false,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const title = formTitle || "this form";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="already-submitted-title"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md overflow-hidden rounded-xl border bg-white shadow-xl ${APPLICATION_THEME.section.wrap}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex items-start gap-3 px-5 py-4 border-b ${APPLICATION_THEME.section.head}`}
        >
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100">
            <DocumentsIcon size={18} />
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <h3
              id="already-submitted-title"
              className={`text-base font-semibold ${APPLICATION_THEME.section.title}`}
            >
              Form already submitted
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              You already have a submission on file
            </p>
          </div>
        </div>

        <div className="px-5 py-4">
          <p className="text-sm leading-relaxed text-slate-600">
            You have already submitted{" "}
            <span className="font-semibold text-slate-800">{title}</span>.
            {allowMultiple
              ? " You can view copies on file, submit the latest again, or start a new form."
              : " Would you like to view your submitted forms, or submit this form again?"}
          </p>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 py-4 border-t border-slate-100 bg-slate-50/80">
          <button
            type="button"
            onClick={onGoToForms}
            className={`inline-flex h-9 items-center justify-center px-4 text-sm font-semibold rounded-md border transition-colors ${APPLICATION_THEME.button.outline}`}
          >
            Go to Forms
          </button>
          {allowMultiple ? (
            <button
              type="button"
              onClick={onAddNewForm}
              className={`inline-flex h-9 items-center justify-center px-4 text-sm font-semibold rounded-md border transition-colors ${APPLICATION_THEME.button.outline}`}
            >
              Add New Form
            </button>
          ) : null}
          <button
            type="button"
            onClick={onFillAgain}
            className={`inline-flex h-9 items-center justify-center px-4 text-sm font-semibold rounded-md transition-colors ${APPLICATION_THEME.button.primary}`}
          >
            Submit Again
          </button>
        </div>
      </div>
    </div>
  );
};

AlreadyFilledFormModal.propTypes = {
  isOpen: PropTypes.bool,
  formTitle: PropTypes.string,
  onClose: PropTypes.func,
  onGoToForms: PropTypes.func,
  onFillAgain: PropTypes.func,
  onAddNewForm: PropTypes.func,
  allowMultiple: PropTypes.bool,
};

export default AlreadyFilledFormModal;
